import { Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { typeLabel, money, STATUS_META } from "@/data/constants";
import { MapPin, Layers, Maximize, Lock, Heart, ShieldCheck, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useFavorites } from "@/hooks/useFavorites";

const FALLBACK =
  "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

export const PropertyCard = ({ listing, index = 0 }) => {
  const cover = listing.photos?.length ? fileUrl(listing.photos[0]) : FALLBACK;
  const { isFavorite, toggle } = useFavorites();
  const faved = isFavorite(listing.id);
  const status = listing.status && listing.status !== "active" ? STATUS_META[listing.status] : null;

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`property-card-${listing.id}`}
      className="group rento-fade-up block overflow-hidden rounded-2xl border border-border/60 bg-white transition-shadow duration-300 hover:shadow-[0_18px_50px_-20px_rgba(44,56,48,0.35)]"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#EDE9E1]">
        <img
          src={cover}
          alt={listing.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = FALLBACK;
          }}
        />
        <div className="absolute left-3 top-3 flex gap-2">
          <Badge className="rounded-full border-0 bg-[#2C3830] text-white capitalize">
            {listing.listing_type === "short_term" ? "Short Stay" : `For ${listing.listing_type}`}
          </Badge>
          {status && (
            <Badge
              className="rounded-full border-0 text-white"
              style={{ backgroundColor: status.color }}
            >
              {status.label}
            </Badge>
          )}
          {listing.owner_verified ? (
            <Badge data-testid="verified-badge" className="rounded-full border-0 bg-[#1D4ED8] text-white"><ShieldCheck className="mr-1 h-3 w-3" /> Verified</Badge>
          ) : (
            <Badge data-testid="unverified-badge" className="rounded-full border-0 bg-[#9CA3AF] text-white">Unverified</Badge>
          )}
        </div>
        <button
          data-testid={`fav-toggle-${listing.id}`}
          onClick={(e) => {
            e.preventDefault();
            toggle(listing.id);
          }}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 backdrop-blur transition-transform hover:scale-110"
          title={faved ? "Remove from saved" : "Save property"}
        >
          <Heart className={`h-4 w-4 ${faved ? "fill-[#D4A373] text-[#D4A373]" : "text-[#2C3830]"}`} />
        </button>
        {listing.photos?.length > 1 && (
          <span className="absolute bottom-3 left-3 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white backdrop-blur">
            {listing.photos.length} photos
          </span>
        )}
      </div>

      <div className="space-y-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-lg font-medium leading-snug text-[#1A1A1A] line-clamp-1">
            {listing.title}
          </h3>
        </div>

        <div className="flex items-center gap-1.5 text-sm text-[#6B7280]">
          <MapPin className="h-4 w-4 shrink-0 text-[#D4A373]" />
          <span className="line-clamp-1">
            {listing.estate ? `${listing.estate}, ` : ""}
            {listing.town}, {listing.county}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-[#6B7280]">
          <span className="rounded-full bg-[#F7F5F0] px-3 py-1 font-medium text-[#2C3830]">
            {typeLabel(listing.property_type)}
          </span>
          {listing.size && (
            <span className="flex items-center gap-1 rounded-full bg-[#F7F5F0] px-3 py-1">
              <Maximize className="h-3 w-3" /> {listing.size}
            </span>
          )}
          {listing.is_storey && listing.floor_number != null && (
            <span className="flex items-center gap-1 rounded-full bg-[#F7F5F0] px-3 py-1">
              <Layers className="h-3 w-3" /> Floor {listing.floor_number}
            </span>
          )}
          {listing.rating_count > 0 && (
            <span data-testid="card-rating" className="flex items-center gap-1 rounded-full bg-[#F7F5F0] px-3 py-1 text-[#2C3830]">
              <Star className="h-3 w-3 fill-[#D4A373] text-[#D4A373]" /> {listing.rating_avg} ({listing.rating_count})
            </span>
          )}
        </div>

        <div className="flex items-end justify-between border-t border-border/60 pt-3">
          <div>
            <span className="font-display text-xl font-semibold text-[#2C3830]">
              {money(listing.price, listing.currency)}
            </span>
            {listing.listing_type === "rent" && (
              <span className="text-sm text-[#6B7280]">/mo</span>
            )}
            {listing.listing_type === "short_term" && (
              <span className="text-sm text-[#6B7280]">/night</span>
            )}
          </div>
          <span className="flex items-center gap-1 text-xs font-medium text-[#D4A373]">
            <Lock className="h-3 w-3" /> Contact locked
          </span>
        </div>
      </div>
    </Link>
  );
};
